import { Command } from 'cmdk';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { cn } from '../lib/cn';
import { ClippedText } from './clipped-text';
import { Highlight } from './highlight';
import { CheckIcon, ChevronDownIcon, PlusIcon, SearchIcon } from './icons';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface IComboboxOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface IComboboxSearch {
  key: readonly unknown[];
  find: (q: string) => Promise<IComboboxOption[]>;
}

interface IComboboxProps {
  value: string;
  onChange: (next: string) => void;
  options: readonly IComboboxOption[];
  noun: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  className?: string;
  action?: { label: string; onSelect: () => void };
  /**
   * Typed text goes to the server: `options` is only the first page, so a name past it is
   * found by asking. `key` names the list in the query cache.
   */
  search?: IComboboxSearch;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const SEARCH_DEBOUNCE_MS = 250;

const useDebounced = (value: string, ms: number): string => {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
};

// Untyped, only the first rows mount; typing searches all of them.
const FIRST = 50;

// Substring of the name, never cmdk's fuzzy score: "prod" must not match "Stripe webhook dev".
const inName = (_value: string, search: string, keywords?: string[]) =>
  (keywords?.[0] ?? '').toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0;

// Offsets, not scrollIntoView: that also scrolls the step panel behind the popover, and the
// open animation's scale skews anything measured on screen.
const centre = (item: HTMLElement | null) => {
  const list = item?.closest<HTMLElement>('[cmdk-list]');
  if (!item || !list) return;
  list.scrollTop = item.offsetTop - (list.clientHeight - item.offsetHeight) / 2;
};

export const Combobox = ({
  value,
  onChange,
  options,
  noun,
  placeholder = 'Pick one',
  disabled,
  invalid,
  id,
  className,
  action,
  search,
  ...aria
}: IComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(value);
  // A row found by searching is not in `options`; its name is kept once picked.
  const [picked, setPicked] = useState<IComboboxOption | null>(null);
  const selected =
    (picked?.value === value ? picked : undefined) ??
    options.find((option) => option.value === value);
  const typed = query.trim();
  const asked = useDebounced(typed, SEARCH_DEBOUNCE_MS);
  const found = useQuery({
    queryKey: [...(search?.key ?? []), 'search', asked],
    queryFn: () => (search ? search.find(asked) : Promise.resolve([])),
    enabled: open && !!search && asked !== '',
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const remote = !!search && typed !== '';
  const searching = remote && (asked !== typed || found.isFetching);

  const shown = remote
    ? (found.data ?? [])
    : typed
      ? options
      : options
          .slice(0, FIRST)
          .concat(selected && options.indexOf(selected) >= FIRST ? [selected] : []);
  const hidden = typed ? 0 : options.length - shown.length;

  const show = (next: boolean, initial = '') => {
    setQuery(initial);
    setActive(value);
    setOpen(next);
  };

  const typeToOpen = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key.length !== 1 || event.key === ' ' || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    show(true, event.key);
  };

  return (
    <Popover open={open} onOpenChange={(next) => show(next)}>
      <PopoverTrigger
        id={id}
        disabled={disabled}
        onKeyDown={typeToOpen}
        aria-invalid={invalid || undefined}
        className={cn(
          'flex h-control w-full items-center gap-3 rounded-control border bg-field px-input',
          'text-left text-body',
          'focus:border-border-focus focus:ring-3 focus:ring-focus focus:outline-none',
          'data-[state=open]:border-border-focus',
          disabled
            ? 'border-border-muted bg-field-muted text-muted'
            : invalid
              ? 'border-danger text-primary'
              : 'border-border-raised text-primary',
          className,
        )}
        {...aria}
      >
        {selected ? (
          <ClippedText text={selected.label} className="grow" />
        ) : (
          <span className="min-w-0 grow truncate text-faint">{placeholder}</span>
        )}
        <span className="flex shrink-0 text-faint">
          <ChevronDownIcon size={13} />
        </span>
      </PopoverTrigger>

      <PopoverContent
        className="w-(--radix-popover-trigger-width) rounded-panel border-border-raised"
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Command
          filter={inName}
          shouldFilter={!remote}
          value={active}
          onValueChange={setActive}
          loop
        >
          <Command.Input asChild value={query} onValueChange={setQuery}>
            <Input
              bare
              size="row"
              leading={<SearchIcon size={14} />}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Find a ${noun} by name`}
              className="border-b border-border-raised"
            />
          </Command.Input>

          <Command.List className="relative max-h-combobox-list overflow-y-auto p-label">
            <Command.Empty className="px-inset py-label text-data text-muted">
              {searching ? `Looking for “${typed}”…` : `No ${noun} matches “${typed}”`}
            </Command.Empty>
            {shown.map((option) => {
              const isSelected = option.value === value;
              return (
                <Command.Item
                  key={option.value}
                  ref={isSelected ? centre : undefined}
                  value={option.value}
                  keywords={[option.label]}
                  disabled={option.disabled}
                  onSelect={() => {
                    setPicked(option);
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex min-h-menu-item cursor-default items-center gap-inset rounded-item px-inset py-label',
                    'text-body data-[selected=true]:bg-hover-raised',
                    '[&_svg]:size-icon-menu',
                    isSelected ? 'text-primary' : 'text-muted data-[selected=true]:text-primary',
                    'data-[disabled=true]:opacity-70',
                  )}
                >
                  <span className="flex min-w-0 grow flex-col">
                    <span className="wrap-anywhere">
                      <Highlight text={option.label} query={typed} />
                    </span>
                    {option.description && (
                      <span className="text-meta leading-17 wrap-anywhere text-muted">
                        {option.description}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <span className="flex shrink-0 text-accent">
                      <CheckIcon />
                    </span>
                  )}
                </Command.Item>
              );
            })}
            {hidden > 0 && (
              <p className="px-inset pt-label pb-hair text-meta text-faint">
                {hidden.toLocaleString('en-US')} more — type to find them.
              </p>
            )}
          </Command.List>

          {action && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                action.onSelect();
              }}
              className={cn(
                'flex min-h-menu-item w-full items-center gap-inset border-t border-border-raised px-input',
                'text-body text-muted hover:bg-hover-raised hover:text-primary [&_svg]:size-icon-menu',
              )}
            >
              <PlusIcon />
              {action.label}
            </button>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};
